<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useDirections } from '~/composables/orga/useDirections'
import { useDomaines } from '~/composables/orga/useDomaine'
import { useAgences } from '~/composables/orga/useAgence'
import { useEquipes } from '~/composables/equipe/useEquipe'
import { useBreadcrumbLabelsStore } from '~~/stores/breadcrumbLabels'
import { isConnectionFlowRoutePath } from '~/composables/ui/useAppToolboxAvailability'
import { getHttpStatusFromUnknown } from '~/composables/ui/useAppError'
import {
  buildDirectionTrail,
  buildEquipeTrail,
  expectedSkeletonSegmentCount,
  isEquipeDeDomaine,
  resolveEntityLabel,
  type BreadcrumbItem,
  type BreadcrumbRouteKeys,
  type BreadcrumbTrailLabels,
} from '~/utils/orga/breadcrumbTrail'

const route = useRoute()
const labelsStore = useBreadcrumbLabelsStore()
const { fetchDirectionById } = useDirections()
const { fetchDomaineById } = useDomaines()
const { fetchAgenceById } = useAgences()
const { fetchEquipeById } = useEquipes()

const path = computed(() => route.path || '')
const dir = computed(() => decodeURIComponent(String(route.params.direction ?? '')))
const dom = computed(() => decodeURIComponent(String(route.params.domaine ?? '')))
const age = computed(() => decodeURIComponent(String(route.params.agence ?? '')))
const equ = computed(() => decodeURIComponent(String(route.params.equipe ?? '')))

const items = ref<BreadcrumbItem[]>([])
const loading = ref(false)

function currentRouteKeys(): BreadcrumbRouteKeys {
  return { dir: dir.value, dom: dom.value, age: age.value, equ: equ.value }
}

function rememberLabel(label: string, persist: () => void) {
  if (label) persist()
}

async function loadDirectionItems(routeKeys: BreadcrumbRouteKeys): Promise<BreadcrumbItem[]> {
  const res = await fetchDirectionById(routeKeys.dir)
  const label = resolveEntityLabel(
    res?.direction?.dirLib,
    res?.direction?.dirLibLong,
    routeKeys.dir
  )
  rememberLabel(label, () => labelsStore.setDirectionLabel(routeKeys.dir, label))
  return buildDirectionTrail(label)
}

async function resolveCurrentEquipeLabel(routeKeys: BreadcrumbRouteKeys): Promise<string | undefined> {
  if (routeKeys.equ === routeKeys.age) return undefined
  const idEquipeCourante = `${routeKeys.dir}~${routeKeys.dom}~${routeKeys.age}~${routeKeys.equ}`
  const storedLabel = labelsStore.getEquipeLabel(idEquipeCourante)
  if (storedLabel) return storedLabel
  const equRes = await fetchEquipeById(routeKeys.dir, routeKeys.dom, routeKeys.age, routeKeys.equ, {
    page: 1,
    limit: 1,
  })
  const equCourantLabel =
    equRes?.equipe?.equLib?.trim() || labelsStore.getEquipeLabel(idEquipeCourante) || routeKeys.equ
  rememberLabel(equCourantLabel, () => labelsStore.setEquipeLabel(idEquipeCourante, equCourantLabel))
  return equCourantLabel || routeKeys.equ
}

async function loadEquipeItems(routeKeys: BreadcrumbRouteKeys): Promise<BreadcrumbItem[]> {
  const idDomaine = `${routeKeys.dir}~${routeKeys.dom}`
  const idAgence = `${routeKeys.dir}~${routeKeys.dom}~${routeKeys.age}`
  const equipeDeDomaine = isEquipeDeDomaine(routeKeys)

  const [resDir, resDom, resAg] = await Promise.all([
    fetchDirectionById(routeKeys.dir),
    fetchDomaineById(idDomaine),
    equipeDeDomaine ? Promise.resolve(null) : fetchAgenceById(idAgence),
  ])

  const labels: BreadcrumbTrailLabels = {
    dirLabel: resolveEntityLabel(resDir?.direction?.dirLib, resDir?.direction?.dirLibLong, routeKeys.dir),
    domLabel: resolveEntityLabel(resDom?.domaine?.domLib, resDom?.domaine?.domLibLong, routeKeys.dom),
    ageLabel: resolveEntityLabel(resAg?.agence?.ageLib, resAg?.agence?.ageLibLong, routeKeys.age),
  }
  rememberLabel(labels.dirLabel, () => labelsStore.setDirectionLabel(routeKeys.dir, labels.dirLabel))
  rememberLabel(labels.domLabel, () => labelsStore.setDomaineLabel(idDomaine, labels.domLabel))
  rememberLabel(labels.ageLabel, () => labelsStore.setAgenceLabel(idAgence, labels.ageLabel))
  labels.equLabel = await resolveCurrentEquipeLabel(routeKeys)
  return buildEquipeTrail(routeKeys, labels)
}

function itemsFromStoreAfterError(errorCaught: unknown, routeKeys: BreadcrumbRouteKeys): BreadcrumbItem[] {
  if (getHttpStatusFromUnknown(errorCaught) === 404) return []
  if (routeKeys.dir && !routeKeys.dom) {
    return buildDirectionTrail(labelsStore.getDirectionLabel(routeKeys.dir) ?? routeKeys.dir)
  }
  if (routeKeys.dir && routeKeys.dom && routeKeys.age && routeKeys.equ) {
    const idDomaine = `${routeKeys.dir}~${routeKeys.dom}`
    const idAgence = `${routeKeys.dir}~${routeKeys.dom}~${routeKeys.age}`
    const idEquipeCourante = `${routeKeys.dir}~${routeKeys.dom}~${routeKeys.age}~${routeKeys.equ}`
    return buildEquipeTrail(routeKeys, {
      dirLabel: labelsStore.getDirectionLabel(routeKeys.dir) ?? routeKeys.dir,
      domLabel: labelsStore.getDomaineLabel(idDomaine) ?? routeKeys.dom,
      ageLabel: labelsStore.getAgenceLabel(idAgence) ?? routeKeys.age,
      equLabel: labelsStore.getEquipeLabel(idEquipeCourante) ?? routeKeys.equ,
    })
  }
  return []
}

async function load() {
  const currentPath = path.value
  if (!currentPath || currentPath === '/') {
    items.value = []
    loading.value = false
    labelsStore.setHoverPreviewDomaine(null)
    return
  }

  loading.value = true

  if (import.meta.server) {
    return
  }

  if (dir.value && dom.value) {
    labelsStore.setHoverPreviewDomaine(null)
  }

  const routeKeys = currentRouteKeys()

  try {
    if (routeKeys.dir && !routeKeys.dom) {
      items.value = await loadDirectionItems(routeKeys)
      return
    }
    if (routeKeys.dir && routeKeys.dom && routeKeys.age && routeKeys.equ) {
      items.value = await loadEquipeItems(routeKeys)
      return
    }
    items.value = []
  } catch (errorCaught) {
    console.error('[OrgaBreadcrumb] load error', errorCaught)
    items.value = itemsFromStoreAfterError(errorCaught, routeKeys)
  } finally {
    loading.value = false
  }
}

watch([path, dir, dom, age, equ], load, { immediate: true })

const showBreadcrumbArea = computed(function computeShowBreadcrumbArea(): boolean {
  const routePath = path.value
  return Boolean(routePath && routePath !== '/' && !isConnectionFlowRoutePath(routePath))
})
const showSkeleton = computed(() => loading.value && showBreadcrumbArea.value)

/** Nombre de segments attendus pour le squelette (4 ou 5 selon la vue agence / équipe). */
const skeletonSegmentCount = computed(() => expectedSkeletonSegmentCount(currentRouteKeys()))

/** Fil d’Ariane + segment domaine en aperçu au survol (page direction uniquement) */
const displayItems = computed(() => {
  const preview = labelsStore.hoverPreviewDomaine
  if (!dir.value || dom.value || !preview?.label) return items.value
  return [...items.value, { label: preview.label }]
})

function isPreviewItem(index: number) {
  return Boolean(dir.value && !dom.value && labelsStore.hoverPreviewDomaine && index === items.value.length)
}

</script>

<template>
  <div v-if="showBreadcrumbArea && (showSkeleton || displayItems.length > 0)" class="orga-breadcrumb-wrap">
    <template v-if="showSkeleton">
      <nav class="orga-breadcrumb orga-breadcrumb--skeleton" aria-label="Fil d'Ariane (chargement)">
        <ol class="orga-breadcrumb__list">
          <li
            v-for="n in skeletonSegmentCount"
            :key="'sk-' + n"
            class="orga-breadcrumb__item"
          >
            <template v-if="n > 1">
              <span class="orga-breadcrumb__separator" aria-hidden="true">&gt;</span>
            </template>
            <span class="orga-breadcrumb__skeleton-segment" aria-hidden="true" />
          </li>
        </ol>
      </nav>
    </template>
    <template v-else>
      <nav
        v-if="displayItems.length > 0"
        class="orga-breadcrumb"
        aria-label="Fil d'Ariane"
      >
        <ol class="orga-breadcrumb__list">
          <li
            v-for="(item, index) in displayItems"
            :key="isPreviewItem(index) ? 'preview' : index"
            class="orga-breadcrumb__item"
            :class="{ 'orga-breadcrumb__item--preview': isPreviewItem(index) }"
          >
            <template v-if="index > 0">
              <span class="orga-breadcrumb__separator" aria-hidden="true">&gt;</span>
            </template>
            <template v-if="item.to">
              <a
                :href="item.to"
                class="orga-breadcrumb__link"
                @click.prevent="navigateTo(item.to)"
              >
                {{ item.label }}
              </a>
            </template>
            <span
              v-else
              class="orga-breadcrumb__current"
            >
              {{ item.label }}
            </span>
          </li>
        </ol>
      </nav>
    </template>
  </div>
</template>

<style scoped lang="scss">
@use "~/assets/scss/partials/elpdl/colors" as c;
@use "~/assets/scss/partials/elpdl/spacing" as *;

.orga-breadcrumb-wrap {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0 $spacing-2xs;
  margin-bottom: $spacing-m;
  min-width: 0;
  font-family: "Public Sans", sans-serif;
  font-size: 0.875rem;
  line-height: 1rem;
}

.orga-breadcrumb {
  margin-bottom: 0;
  flex: 0 1 auto;
  min-width: 0;
}

.orga-breadcrumb__list {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin: 0;
  padding: 0;
  list-style: none;
  gap: 0 $spacing-2xs;
}

.orga-breadcrumb__item {
  display: inline-flex;
  align-items: center;
  gap: $spacing-2xs;
  flex-shrink: 0;
}

.orga-breadcrumb__separator {
  color: c.$enedis-neutral-300;
  font-weight: 400;
  user-select: none;
  flex-shrink: 0;
}

.orga-breadcrumb__link {
  color: var(--color-link);
  text-decoration: none;
  font-weight: 400;

  &:hover {
    color: var(--color-link-hover);
  }

  &:focus-visible {
    outline: 2px solid var(--color-link);
    outline-offset: 2px;
  }
}

.orga-breadcrumb__current {
  color: var(--color-text);
  font-weight: 600;
  text-decoration: none;
}

/* Segment domaine en aperçu au survol d’une colonne (page direction) */
.orga-breadcrumb__item--preview {
  animation: breadcrumb-preview-in 0.25s ease-out;
}

@keyframes breadcrumb-preview-in {
  from {
    opacity: 0;
    transform: translateX(-6px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.orga-breadcrumb__skeleton-segment {
  display: inline-block;
  height: 1rem;
  width: 4rem;
  max-width: 100%;
  border-radius: 2px;
  background: var(--color-bg-muted);
  animation: orga-breadcrumb-shimmer 1.2s ease-in-out infinite;
}

.orga-breadcrumb--skeleton .orga-breadcrumb__item:nth-of-type(odd) .orga-breadcrumb__skeleton-segment {
  width: 3rem;
}

.orga-breadcrumb--skeleton .orga-breadcrumb__item:nth-of-type(3) .orga-breadcrumb__skeleton-segment {
  width: 5rem;
}

@keyframes orga-breadcrumb-shimmer {
  0%,
  100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}
</style>
